/**
 * Hydra Effect Node Definitions
 *
 * Single-input transforms: rotate, scale, brightness, etc.
 */


import { HydraEffectNode } from './HydraEffectNode';
import type { NodeDef } from '@runtime/IRuntime';
import { HydraConfig } from './modulates';
import { internals, params } from '@/nodes/TypedEndpoints';
import { Dependencies } from '@/nodes/Dependencies';

const hydraInOut = internals({
  hydraIn: { dataType: 'hydra', isInput: true },
  hydraOut: { dataType: 'hydra', isOutput: true }
})

const effectConfigs: HydraConfig[] = [
  // Colour effects
  {
    type: 'Brightness',
    shortName: 'bright',
    hydraFn: 'brightness',
    params: {
      amount: { value: 0.4, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'Contrast',
    shortName: 'contrast',
    hydraFn: 'contrast',
    params: {
      amount: { value: 1.6, min: 0, max: 4, step: 0.01 },
    }
  },
  {
    type: 'Saturate',
    shortName: 'sat',
    hydraFn: 'saturate',
    params:{
      amount: {  value: 2, min: 0, max: 4, step: 0.01 },
    },
  },
  {
    type: 'Hue',
    shortName: 'hue',
    hydraFn: 'hue',
    params: {
      hue: { value: 0.5, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'Invert',
    shortName: 'invert',
    hydraFn: 'invert',
    params:{
      amount: { value: 1, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'Colorama',
    shortName: 'colorama',
    hydraFn: 'colorama',
    params: {
      amount: { value: 0.005, min: 0, max: 1, step: 0.001 },
    },
  },
  {
    type: 'Posterize',
    shortName: 'poster',
    hydraFn: 'posterize',
    params: {
      bins: {  value: 3, min: 1, max: 20, step: 1 },
      gamma: { value: 0.6, min: 0, max: 2, step: 0.01 },
    },
  },
  {
    type: 'Luma',
    shortName: 'luma',
    hydraFn: 'luma',
    params: {
      threshold: { value: 0.5, min: 0, max: 1, step: 0.01 },
      tolerance: { value: 0.1, min: 0, max: 1, step: 0.01 },
    }
  },
  {
    type: 'Thresh',
    shortName: 'thresh',
    hydraFn: 'thresh',
    params: {
      threshold: { dataType: 'number', value: 0.5, min: 0, max: 1, step: 0.01 },
      tolerance: { dataType: 'number', value: 0.04, min: 0, max: 1, step: 0.01 }
    }
  },
  {
    type: 'Color',
    shortName: 'color',
    hydraFn: 'color',
    params: {
      r: { dataType: 'number', value: 1, min: 0, max: 1, step: 0.01 },
      g: { dataType: 'number', value: 1, min: 0, max: 1, step: 0.01 },
      b: { dataType: 'number', value: 1, min: 0, max: 1, step: 0.01 },
    }
  },
  // Geometry effects
  {
    type: 'Rotate',
    shortName: 'rotate',
    hydraFn: 'rotate',
    params: {
      angle: { dataType: 'number', value: 0.1, min: -6.28, max: 6.28, step: 0.01 },
      speed: { dataType: 'number', value: 0, min: -2, max: 2, step: 0.01 },
    }
  },
  {
    type: 'Scale',
    shortName: 'scale',
    hydraFn: 'scale',
    params: {
      amount: { dataType: 'number', value: 1.5, min: 0.01, max: 10, step: 0.01 },
      xMult: { dataType: 'number', value: 1, min: 0.01, max: 10, step: 0.01 },
      yMult: { dataType: 'number', value: 1, min: 0.01, max: 10, step: 0.01 },
    }
  },
  {
    type: 'Kaleid',
    shortName: 'kaleid',
    hydraFn: 'kaleid',
    params: {
      sides: { dataType: 'number', value: 4, min: 1, max: 50, step: 1 },
    }
  },
  {
    type: 'Pixelate',
    shortName: 'pixel',
    hydraFn: 'pixelate',
    params: {
      x: { value: 20, min: 1, max: 100, step: 1 },
      y: { value: 20, min: 1, max: 100, step: 1 },
    }
  },
  {
    type: 'Repeat',
    shortName: 'repeat',
    hydraFn: 'repeat',
    params: {
      repeatX: {value: 3, min: 1, max: 20, step: 1 },
      repeatY: {value: 3, min: 1, max: 20, step: 1 },
      offsetX: {value: 0, min: 0, max: 2, step: 0.01 },
      offsetY: {value: 0, min: 0, max: 2, step: 0.01 },
      hydraOut: { dataType: 'hydra', isOutput: true },
    }
  },
  {
    type: 'ScrollX',
    shortName: 'scrollX',
    hydraFn: 'scrollX',
    params: {
      scroll: { value: 0.5, min: -2, max: 2, step: 0.01 },
      speed: { value: 0, min: -2, max: 2, step: 0.01 },
    }
  },
  {
    type: 'ScrollY',
    shortName: 'scrollY',
    hydraFn: 'scrollY',
    params: {
     
      scroll: { value: 0.5, min: -2, max: 2, step: 0.01 },
      speed: { value: 0, min: -2, max: 2, step: 0.01 },

    }
  }
];

// Generate node definitions from configs
export const effectNodes: NodeDef[] = effectConfigs.map(config => {
  
  const endpoints = {
    ...params(config.params),
    ...hydraInOut
  }
  
  return {
  type: config.type,
  shortName: config.shortName,
  category: 'effect',
  endpoints,
  ctor: class extends HydraEffectNode<typeof endpoints> {
    constructor(id: string, deps: Dependencies) {
      super(id, deps, { 
        hydraFn: config.hydraFn, 
        fnArgs: Object.keys(config.params)
      });
    }
  }
}});
